<app-side-menu>
  <div class="accounts-receivable-view-content">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="page-header">
          <div class="d-flex align-items-center">
            <button 
              type="button"
              class="btn btn-outline-secondary me-3"
              (click)="goBack()">
              <i class="fas fa-arrow-left me-2"></i>
              Voltar
            </button>
            <div>
              <h1 class="page-title">
                <i class="fas fa-receipt me-3"></i>
                Visualizar Conta a Receber
              </h1>
              <p class="page-subtitle">Detalhes completos da conta a receber</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
      <p class="mt-3">Carregando dados da conta a receber...</p>
    </div>

    <!-- Error State -->
    <div *ngIf="error && !loading" class="alert alert-danger" role="alert">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>
        <button 
          type="button"
          class="btn btn-outline-danger btn-sm"
          (click)="loadAccountsReceivable()">
          <i class="fas fa-redo me-1"></i>
          Tentar Novamente
        </button>
      </div>
    </div>

    <!-- Aviso: dados de recebimento limpos (cobrança não encontrada no Asaas) -->
    <div *ngIf="receiptClearedWarning && accountsReceivable && !loading" class="alert alert-warning alert-dismissible fade show mb-4" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      <strong>Atenção:</strong> {{ receiptClearedWarning }}
      <button type="button" class="btn-close" aria-label="Fechar" (click)="receiptClearedWarning = null"></button>
    </div>

    <!-- Accounts Receivable Details -->
    <div *ngIf="accountsReceivable && !loading" class="row g-4">
      <!-- Informações do Documento -->
      <div class="col-lg-6">
        <div class="card h-100">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-file-alt me-2"></i>
              Informações do Documento
            </h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6" *ngIf="accountsReceivable.contractNumber">
                <div class="info-item">
                  <label class="form-label">Número do Contrato</label>
                  <div class="info-value">{{ accountsReceivable.contractNumber }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.ourNumber">
                <div class="info-item">
                  <label class="form-label">Identificação Bancária</label>
                  <div class="info-value">{{ accountsReceivable.ourNumber }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.bankReferenceNumber">
                <div class="info-item">
                  <label class="form-label">Referência do Banco</label>
                  <div class="info-value">{{ accountsReceivable.bankReferenceNumber }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Número do Documento</label>
                  <div class="info-value document-number">{{ accountsReceivable.documentNumber }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Tipo</label>
                  <div class="info-value">{{ accountsReceivable.documentType }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Data de Emissão</label>
                  <div class="info-value">{{ formatDate(accountsReceivable.issueDate) }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Data de Vencimento</label>
                  <div class="info-value due-date" [class.overdue]="isOverdue()">
                    {{ formatDate(accountsReceivable.dueDate) }}
                    <span *ngIf="isOverdue()" class="overdue-badge">
                      <i class="fas fa-exclamation-triangle"></i>
                      Vencido
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Nota Fiscal</label>
                  <div class="info-value">{{ accountsReceivable.invoiceNumber || 'N/A' }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Pedido de Venda</label>
                  <div class="info-value">{{ accountsReceivable.saleOrderNumber || 'N/A' }}</div>
                </div>
              </div>
              <div class="col-12">
                <div class="info-item">
                  <label class="form-label">Descrição</label>
                  <div class="info-value">{{ accountsReceivable.description || 'N/A' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Valores e Status -->
      <div class="col-lg-6">
        <div class="card h-100">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-calculator me-2"></i>
              Valores e Status
            </h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Valor Original</label>
                  <div class="info-value amount">{{ formatCurrency(accountsReceivable.originalAmount) }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Status</label>
                  <div class="info-value">
                    <span class="status-badge" [ngClass]="getStatusClass(accountsReceivable.status)">
                      {{ accountsReceivable.status }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.discountAmount">
                <div class="info-item">
                  <label class="form-label">Desconto</label>
                  <div class="info-value discount">{{ formatCurrency(accountsReceivable.discountAmount) }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.interestAmount">
                <div class="info-item">
                  <label class="form-label">Juros</label>
                  <div class="info-value interest">{{ formatCurrency(accountsReceivable.interestAmount) }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.fineAmount">
                <div class="info-item">
                  <label class="form-label">Multa</label>
                  <div class="info-value fine">{{ formatCurrency(accountsReceivable.fineAmount) }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Valor a Receber</label>
                  <div class="info-value amount-primary">{{ formatCurrency(accountsReceivable.amountToReceive) }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.amountReceived">
                <div class="info-item">
                  <label class="form-label">Valor Recebido</label>
                  <div class="info-value amount-received">{{ formatCurrency(accountsReceivable.amountReceived) }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Valor Restante</label>
                  <div class="info-value amount-remaining">{{ formatCurrency(accountsReceivable.remainingAmount) }}</div>
                </div>
              </div>
              <div class="col-12" *ngIf="hasChargePending()">
                <div class="info-item">
                  <label class="form-label">Em cobrança</label>
                  <div class="info-value text-info">
                    {{ formatCurrency(accountsReceivable.amountToReceive) }}
                    <small class="text-muted">(aguardando pagamento; se não for pago ou for cancelado, o saldo volta)</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cliente e Classificação -->
      <div class="col-lg-6">
        <div class="card h-100">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-user me-2"></i>
              Cliente e Classificação
            </h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-12">
                <div class="info-item">
                  <label class="form-label">Cliente</label>
                  <div class="info-value customer-name">{{ accountsReceivable.customerName || 'N/A' }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Categoria</label>
                  <div class="info-value category">{{ accountsReceivable.category }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.subcategory">
                <div class="info-item">
                  <label class="form-label">Subcategoria</label>
                  <div class="info-value">{{ accountsReceivable.subcategory }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Centro de Custo</label>
                  <div class="info-value">{{ accountsReceivable.costCenterName || 'N/A' }}</div>
                </div>
              </div>
              <div class="col-12" *ngIf="accountsReceivable.installmentNumber && accountsReceivable.totalInstallments">
                <div class="info-item">
                  <label class="form-label">Parcela</label>
                  <div class="info-value installment">
                    {{ accountsReceivable.installmentNumber }} de {{ accountsReceivable.totalInstallments }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detalhes do Recebimento -->
      <div class="col-lg-6">
        <div class="card h-100">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-hand-holding-usd me-2"></i>
              Detalhes do Recebimento
            </h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6" *ngIf="accountsReceivable.bankName">
                <div class="info-item">
                  <label class="form-label">Banco</label>
                  <div class="info-value">{{ accountsReceivable.bankName }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Forma de Recebimento</label>
                  <div class="info-value">{{ accountsReceivable.receiptMethod || 'N/A' }}</div>
                </div>
              </div>
              <div class="col-12" *ngIf="accountsReceivable.receiptPayments && accountsReceivable.receiptPayments.length > 0">
                <div class="info-item">
                  <label class="form-label">Formas de Recebimento</label>
                  <ul class="list-unstyled mb-0">
                    <li *ngFor="let p of accountsReceivable.receiptPayments" class="d-flex justify-content-between py-1">
                      <span>{{ p.paymentMethod }}</span>
                      <span>{{ p.amount | currency:'BRL':'symbol':'1.2-2' }}</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.receiptType">
                <div class="info-item">
                  <label class="form-label">Tipo de Recebimento</label>
                  <div class="info-value">{{ accountsReceivable.receiptType }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item">
                  <label class="form-label">Data de Recebimento</label>
                  <div class="info-value">{{ accountsReceivable.receiptDate ? formatDate(accountsReceivable.receiptDate) : 'N/A' }}</div>
                </div>
              </div>
              <div class="col-12" *ngIf="accountsReceivable.receiptReference">
                <div class="info-item">
                  <label class="form-label">Referência do Recebimento</label>
                  <div class="info-value">{{ accountsReceivable.receiptReference }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.bankSlipNumber">
                <div class="info-item">
                  <label class="form-label">Número do Boleto</label>
                  <div class="info-value">{{ accountsReceivable.bankSlipNumber }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.checkNumber">
                <div class="info-item">
                  <label class="form-label">Número do Cheque</label>
                  <div class="info-value">{{ accountsReceivable.checkNumber }}</div>
                </div>
              </div>
              <div class="col-12" *ngIf="accountsReceivable.receivedByStaffName">
                <div class="info-item">
                  <label class="form-label">Recebido por</label>
                  <div class="info-value">{{ accountsReceivable.receivedByStaffName }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.discountDays !== undefined && accountsReceivable.discountDays !== null">
                <div class="info-item">
                  <label class="form-label">Dias de Desconto</label>
                  <div class="info-value">{{ accountsReceivable.discountDays }}</div>
                </div>
              </div>
              <div class="col-md-6" *ngIf="accountsReceivable.isRecurring">
                <div class="info-item">
                  <label class="form-label">Recorrência</label>
                  <div class="info-value">
                    {{ accountsReceivable.recurrenceFrequency || 'N/A' }}
                    <span *ngIf="accountsReceivable.recurrenceEndDate">
                      — até {{ formatDate(accountsReceivable.recurrenceEndDate) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cobrança Asaas (consulta GET /payments/asaas/charges/{id}) -->
      <div class="col-12" *ngIf="hasAsaasChargeLinked()">
        <div class="card asaas-charge-card">
          <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 class="card-title mb-0">
              <i class="fas fa-barcode me-2"></i>
              Cobrança Asaas
            </h5>
            <span class="badge bg-secondary" *ngIf="asaasChargeId">{{ asaasChargeId }}</span>
          </div>
          <div class="card-body">
            <div *ngIf="loadingAsaasCharge" class="text-center py-3">
              <span class="spinner-border spinner-border-sm text-primary me-2"></span>
              Carregando cobrança no Asaas...
            </div>

            <div class="alert alert-warning mb-3" *ngIf="!loadingAsaasCharge && asaasChargeLoadError">
              {{ asaasChargeLoadError }}
              <button type="button" class="btn btn-sm btn-outline-warning ms-2" (click)="loadAsaasChargeDetails()">
                Tentar novamente
              </button>
            </div>

            <div class="row g-3" *ngIf="!loadingAsaasCharge && asaasChargeDetails">
              <div class="col-md-3">
                <label class="form-label text-muted small">Status (Asaas)</label>
                <div class="fw-semibold">{{ getAsaasChargeStatusLabel() }}</div>
              </div>
              <div class="col-md-3" *ngIf="asaasChargeDetails.billingType">
                <label class="form-label text-muted small">Tipo</label>
                <div class="fw-semibold">{{ asaasChargeDetails.billingType }}</div>
              </div>
              <div class="col-md-3" *ngIf="asaasChargeDetails.value != null">
                <label class="form-label text-muted small">Valor</label>
                <div class="fw-semibold text-success">
                  {{ asaasChargeDetails.value | currency:'BRL':'symbol':'1.2-2' }}
                </div>
              </div>
              <div class="col-md-3" *ngIf="asaasChargeDetails.dueDate">
                <label class="form-label text-muted small">Vencimento (cobrança)</label>
                <div class="fw-semibold">{{ formatDate(asaasChargeDetails.dueDate) }}</div>
              </div>
              <div class="col-md-6" *ngIf="asaasChargeDetails.nossoNumero">
                <label class="form-label text-muted small">Nosso número</label>
                <div class="fw-semibold">{{ asaasChargeDetails.nossoNumero }}</div>
              </div>
              <div class="col-12 d-flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  class="btn btn-primary"
                  (click)="viewAsaasChargeDocument()"
                  [disabled]="!canViewAsaasChargeDocument()">
                  <i class="fas fa-external-link-alt me-2"></i>
                  {{ getAsaasChargeViewButtonLabel() }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="loadAsaasChargeDetails()"
                  [disabled]="loadingAsaasCharge">
                  <i class="fas fa-sync-alt me-2"></i>
                  Atualizar cobrança
                </button>
                <button
                  type="button"
                  class="btn btn-outline-info"
                  (click)="checkAsaasPaymentStatusAndUpdateAccount(false)"
                  [disabled]="checkingPaymentStatus">
                  <i class="fas fa-search-dollar me-2" *ngIf="!checkingPaymentStatus"></i>
                  <i class="fas fa-spinner fa-spin me-2" *ngIf="checkingPaymentStatus"></i>
                  Verificar pagamento no Asaas
                </button>
              </div>
              <p class="text-muted small mb-0" *ngIf="!canViewAsaasChargeDocument()">
                A cobrança foi encontrada, mas o Asaas ainda não retornou o link do boleto/fatura.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Recebimento: botão Receber abre o modal (formas + Asaas/BB/Registrar) -->
      <div class="col-lg-6">
        <div class="card h-100">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-money-bill-wave me-2"></i>
              Recebimento
            </h5>
          </div>
          <div class="card-body">
            <div class="d-grid gap-2">
              <button 
                class="btn btn-success btn-lg" 
                (click)="openReceiveModal()"
                [disabled]="paymentLoading || !canReceive()"
                [title]="!canReceive() && getReceiveBlockedReason() ? getReceiveBlockedReason() : (isOverdue() ? 'Título vencido: é possível gerar nova cobrança (boleto/PIX).' : null)">
                <i class="fas fa-hand-holding-usd me-2"></i>
                Receber
              </button>
              <p *ngIf="!canReceive() && getReceiveBlockedReason()" class="text-muted small mb-0 mt-1">
                <i class="fas fa-lock me-1"></i>
                {{ getReceiveBlockedReason() }}
              </p>
              <p *ngIf="isOverdue() && isPendingOrPartial() && canReceive()" class="text-muted small mb-0 mt-1">
                <i class="fas fa-info-circle me-1"></i>
                Título vencido: cobranças (boleto/PIX) podem ser geradas; no Asaas o vencimento da cobrança usará a data atual.
              </p>

              <hr *ngIf="canCancelAsaasCharge()" class="my-3">

              <button 
                class="btn btn-outline-danger" 
                (click)="cancelAsaasCharge()"
                [disabled]="cancellingCharge || !canCancelAsaasCharge()"
                *ngIf="canCancelAsaasCharge()">
                <i class="fas fa-ban me-2" *ngIf="!cancellingCharge"></i>
                <i class="fas fa-spinner fa-spin me-2" *ngIf="cancellingCharge"></i>
                {{ cancellingCharge ? 'Cancelando...' : 'Cancelar cobrança' }}
                <span *ngIf="!cancellingCharge && getCancelChargeDescription()" class="d-block small mt-1 opacity-75">{{ getCancelChargeDescription() }}</span>
              </button>
            </div>
            <div *ngIf="paymentLoading" class="text-center mt-3">
              <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
              <small class="text-muted d-block">Processando...</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Ações -->
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-cogs me-2"></i>
              Ações
            </h5>
          </div>
          <div class="card-body">
            <div class="d-flex flex-wrap gap-3">
              <button 
                type="button" 
                class="btn btn-primary"
                (click)="editAccountsReceivable()">
                <i class="fas fa-edit me-2"></i>
                Editar
              </button>

              <button 
                type="button" 
                class="btn btn-danger"
                (click)="cancelAsaasCharge()"
                [disabled]="cancellingCharge"
                *ngIf="canCancelAsaasCharge()"
                [title]="getCancelChargeDescription()">
                <i class="fas fa-ban me-2" *ngIf="!cancellingCharge"></i>
                <i class="fas fa-spinner fa-spin me-2" *ngIf="cancellingCharge"></i>
                {{ cancellingCharge ? 'Cancelando...' : 'Cancelar cobrança' }}
              </button>
              
              <button 
                type="button" 
                class="btn btn-danger"
                (click)="deleteAccountsReceivable()">
                <i class="fas fa-trash me-2"></i>
                Excluir
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Histórico de recebimentos -->
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">
              <i class="fas fa-history me-2"></i>
              Histórico de recebimentos
            </h5>
            <button
              *ngIf="selectedPaymentIds.size > 0 && canCancelSelectedPayments()"
              type="button"
              class="btn btn-danger btn-sm"
              (click)="cancelSelectedPayments()"
              [disabled]="cancellingPayments">
              <i class="fas fa-ban me-1" *ngIf="!cancellingPayments"></i>
              <span class="spinner-border spinner-border-sm me-1" *ngIf="cancellingPayments" role="status"></span>
              {{ cancellingPayments ? 'Cancelando...' : 'Cancelar ' + selectedPaymentIds.size + ' selecionado(s)' }}
            </button>
          </div>
          <div class="card-body">

            <div *ngIf="loadingPayments" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
              <span class="ms-2 small text-muted">Carregando...</span>
            </div>
            <div *ngIf="!loadingPayments && paymentHistory.length === 0" class="text-muted small">
              Nenhum recebimento registrado para este título.
            </div>
            <div *ngIf="!loadingPayments && paymentHistory.length > 0" class="table-responsive">
              <table class="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th style="width: 40px;">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        [checked]="allPaymentsSelected()"
                        (change)="toggleSelectAllPayments()"
                        [disabled]="!canCancelSelectedPayments() && selectedPaymentIds.size === 0"
                        title="Selecionar todos">
                    </th>
                    <th>Data</th>
                    <th>Forma</th>
                    <th class="text-end">Valor</th>
                    <th>Referência</th>
                    <th class="text-center">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let p of paymentHistory"
                    [class.table-warning]="isPaymentSelected(p.id)"
                    [class.text-muted]="isPaymentCancelled(p)"
                    [style.opacity]="isPaymentCancelled(p) ? '0.55' : '1'">
                    <td>
                      <input
                        type="checkbox"
                        class="form-check-input"
                        [checked]="isPaymentSelected(p.id)"
                        (change)="togglePaymentSelection(p.id)"
                        [disabled]="getCancelBlockedReason() !== '' || isPaymentCancelled(p)">
                    </td>
                    <td>{{ formatDate(p.paymentDate) }}</td>
                    <td>{{ p.paymentMethod }}</td>
                    <td class="text-end">
                      <s *ngIf="isPaymentCancelled(p)">{{ formatCurrency(p.amount) }}</s>
                      <span *ngIf="!isPaymentCancelled(p)">{{ formatCurrency(p.amount) }}</span>
                    </td>
                    <td>{{ p.reference || '—' }}</td>
                    <td class="text-center">
                      <span [ngClass]="getPaymentStatusBadge(p).cssClass">
                        {{ getPaymentStatusBadge(p).label }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p *ngIf="paymentHistory.length > 0 && !getCancelBlockedReason()" class="small text-muted mt-2 mb-0">
                <i class="fas fa-info-circle me-1"></i>
                Selecione um ou mais recebimentos e clique em "Cancelar selecionados" para desfazê-los. Se houver cobranças Asaas vinculadas (campo Referência iniciando com <code>pay_</code>), elas também serão canceladas no Asaas.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</app-side-menu>

<!-- Asaas Payment Modal -->
<div class="modal fade" [class.show]="showAsaasModal" [style.display]="showAsaasModal ? 'block' : 'none'" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Gerar Cobrança Asaas</h5>
        <button type="button" class="btn-close" (click)="closeAsaasModal()"></button>
      </div>
      <div class="modal-body">
        <form>
          <div class="mb-3">
            <label class="form-label">Forma de Recebimento</label>
            <select class="form-select" [(ngModel)]="asaasBillingType" name="billingType">
              <option value="BOLETO">Boleto Bancário</option>
              <option value="CREDIT_CARD">Cartão de Crédito</option>
              <option value="PIX">PIX</option>
            </select>
          </div>

          <!-- Credit Card Fields -->
          <div *ngIf="asaasBillingType === 'CREDIT_CARD'">
            <div class="mb-3">
              <label class="form-label">Nome no Cartão</label>
              <input type="text" class="form-control" [(ngModel)]="creditCard.holderName" name="holderName" placeholder="Nome impresso no cartão">
            </div>
            <div class="mb-3">
              <label class="form-label">Número do Cartão</label>
              <input type="text" class="form-control" [(ngModel)]="creditCard.number" name="cardNumber" placeholder="0000 0000 0000 0000">
            </div>
            <div class="row">
              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Mês Exp.</label>
                  <input type="text" class="form-control" [(ngModel)]="creditCard.expiryMonth" name="expiryMonth" placeholder="MM" maxlength="2">
                </div>
              </div>
              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Ano Exp.</label>
                  <input type="text" class="form-control" [(ngModel)]="creditCard.expiryYear" name="expiryYear" placeholder="YYYY" maxlength="4">
                </div>
              </div>
              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">CCV</label>
                  <input type="text" class="form-control" [(ngModel)]="creditCard.ccv" name="ccv" placeholder="123" maxlength="4">
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeAsaasModal()">Cancelar</button>
        <button type="button" class="btn btn-primary" (click)="confirmAsaasPayment()">Confirmar</button>
      </div>
    </div>
  </div>
</div>
<div class="modal-backdrop fade show" *ngIf="showAsaasModal"></div>

<!-- Modal Receber: formas de recebimento + escolha Asaas / BB / Registrar -->
<div class="modal fade" [class.show]="showReceiveModal" [style.display]="showReceiveModal ? 'block' : 'none'" tabindex="-1">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="fas fa-hand-holding-usd me-2"></i>
          Receber
        </h5>
        <button type="button" class="btn-close" (click)="closeReceiveModal()"></button>
      </div>
      <div class="modal-body">
        <p class="text-muted mb-3">
          Informe uma ou mais formas de recebimento e o valor em cada uma. Depois escolha se vai gerar a cobrança via Asaas, via Banco do Brasil ou apenas registrar o recebimento.
        </p>
        <div class="mb-3">
          <label class="form-label fw-bold">Valor a receber</label>
          <div class="fs-4 text-success">{{ getAmountToReceiveInModal() | currency:'BRL':'symbol':'1.2-2' }}</div>
        </div>
        <div class="mb-3">
          <label class="form-label">Data do recebimento</label>
          <input type="date" class="form-control" [(ngModel)]="receiveDate" name="receiveDate">
        </div>
        <hr>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="form-label mb-0">Formas de recebimento</label>
          <button type="button" class="btn btn-sm btn-outline-primary" (click)="addReceivePaymentRow()">
            <i class="fas fa-plus me-1"></i>
            Adicionar outra forma
          </button>
        </div>
        <div class="receive-payments-list">
          <div *ngFor="let p of receivePayments; let i = index" class="row g-2 align-items-end mb-2">
            <div class="col-md-5">
              <label class="form-label small">Forma</label>
              <select class="form-select form-select-sm" [(ngModel)]="p.paymentMethod" [name]="'payMethod' + i">
                <option value="">Selecione...</option>
                <option *ngFor="let opt of receivePaymentMethodOptions" [value]="opt">{{ opt }}</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small">Valor (R$)</label>
              <input 
                type="text" 
                class="form-control form-control-sm" 
                [(ngModel)]="p.amount" 
                [name]="'payAmount' + i" 
                appCurrencyShiftMask 
                placeholder="0,00">
            </div>
            <div class="col-md-3">
              <label class="form-label small">Referência (opcional)</label>
              <input type="text" class="form-control form-control-sm" [(ngModel)]="p.reference" [name]="'payRef' + i" placeholder="Ex: ****1234">
            </div>
            <div class="col-md-1">
              <button type="button" class="btn btn-outline-danger btn-sm" (click)="removeReceivePaymentRow(i)" [disabled]="receivePayments.length <= 1" title="Remover">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="mt-3 p-2 rounded bg-light d-flex justify-content-between align-items-center">
          <span>Total informado:</span>
          <strong>{{ getReceiveTotal() | currency:'BRL':'symbol':'1.2-2' }}</strong>
        </div>
        <div class="mt-2 p-2 rounded d-flex justify-content-between align-items-center" [ngClass]="getReceiveBalance() === 0 ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'">
          <span>Saldo:</span>
          <strong>{{ getReceiveBalance() | currency:'BRL':'symbol':'1.2-2' }}</strong>
        </div>
        <p class="small text-muted mt-2 mb-0" *ngIf="getReceiveBalance() !== 0 && payAction === 'register'">
          Você pode registrar recebimento total ou parcial.
        </p>

        <hr class="my-4">
        <label class="form-label fw-bold">Gerar cobrança ou registrar</label>
        <div class="form-check">
          <input class="form-check-input" type="radio" name="payAction" id="payActionAsaas" value="asaas" [(ngModel)]="payAction">
          <label class="form-check-label" for="payActionAsaas">Gerar cobrança via Asaas (boleto, PIX ou cartão)</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" name="payAction" id="payActionBBBoleto" value="bb_boleto" [(ngModel)]="payAction">
          <label class="form-check-label" for="payActionBBBoleto">Gerar boleto via Banco do Brasil</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" name="payAction" id="payActionBBPix" value="bb_pix" [(ngModel)]="payAction">
          <label class="form-check-label" for="payActionBBPix">Gerar PIX via Banco do Brasil</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" name="payAction" id="payActionRegister" value="register" [(ngModel)]="payAction">
          <label class="form-check-label" for="payActionRegister">Apenas registrar recebimento (valor já recebido)</label>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeReceiveModal()">Cancelar</button>
        <button type="button" class="btn btn-primary" (click)="confirmPayAction()" [disabled]="!canConfirmPayAction() || paymentLoading">
          <i class="fas fa-check me-1" *ngIf="!paymentLoading"></i>
          <span class="spinner-border spinner-border-sm me-1" *ngIf="paymentLoading" role="status"></span>
          {{ getPayActionButtonLabel() }}
        </button>
      </div>
    </div>
  </div>
</div>
<div class="modal-backdrop fade show" *ngIf="showReceiveModal"></div>
